<script setup>
import { useAlert } from 'dashboard/composables';
import { OnClickOutside } from '@vueuse/components';
import {
  getInboxClassByType,
  getInboxIconByType,
} from 'dashboard/helper/inbox';
import AddCanned from './AddCanned.vue';
import EditCanned from './EditCanned.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

defineOptions({
  name: 'CannedResponseSettings',
});

// String sentinels so they can never collide with a numeric inbox id.
const INBOX_FILTER_ALL = 'all';
const INBOX_FILTER_NONE = 'none';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showAddPopup = ref(false);
const loading = ref({});
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});
const cannedResponseAPI = ref({ message: '' });

const sortOrder = ref('asc');
const records = computed(() =>
  getters.getSortedCannedResponses.value(sortOrder.value)
);
const uiFlags = computed(() => getters.getUIFlags.value);

const selectedInboxFilter = ref(INBOX_FILTER_ALL);
const isInboxMenuOpen = ref(false);

const inboxes = computed(() => getters['inboxes/getInboxes'].value);

const inboxMenuItems = computed(() => {
  const item = (value, label, icon) => ({
    label,
    value,
    icon,
    action: 'filter',
    isSelected: selectedInboxFilter.value === value,
  });

  return [
    item(INBOX_FILTER_ALL, t('CANNED_MGMT.LIST.FILTER.ALL_INBOXES')),
    item(
      INBOX_FILTER_NONE,
      t('CANNED_MGMT.LIST.FILTER.NO_INBOX'),
      'i-ri-forbid-2-line'
    ),
    ...inboxes.value.map(inbox =>
      item(
        inbox.id,
        inbox.internal_name || inbox.name,
        getInboxIconByType(inbox.channel_type, inbox.phone_number, 'line')
      )
    ),
  ];
});

const activeInboxFilterLabel = computed(() => {
  const activeItem = inboxMenuItems.value.find(
    ({ value }) => value === selectedInboxFilter.value
  );
  return activeItem?.label || t('CANNED_MGMT.LIST.FILTER.ALL_INBOXES');
});

const filteredRecords = computed(() => {
  const selected = selectedInboxFilter.value;

  if (selected === INBOX_FILTER_ALL) {
    return records.value;
  }

  if (selected === INBOX_FILTER_NONE) {
    return records.value.filter(record => !record.inboxes?.length);
  }

  return records.value.filter(record =>
    record.inboxes?.some(inbox => inbox.id === selected)
  );
});

const handleInboxFilter = ({ value }) => {
  selectedInboxFilter.value = value;
  isInboxMenuOpen.value = false;
};

const deleteConfirmText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.short_code} ? `;
});

const toggleSort = () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
};

const fetchCannedResponses = async () => {
  try {
    await store.dispatch('getCannedResponse');
  } catch (error) {
    // Ignore Error
  }
};

onMounted(() => {
  fetchCannedResponses();
  store.dispatch('inboxes/get');
});

const showAlertMessage = message => {
  loading[activeResponse.value.id] = false;
  activeResponse.value = {};
  cannedResponseAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  activeResponse.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteCannedResponse = async id => {
  try {
    await store.dispatch('deleteCannedResponse', id);
    showAlertMessage(t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteCannedResponse(activeResponse.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('CANNED_MGMT.LIST.TABLE_HEADER.SHORT_CODE'),
    t('CANNED_MGMT.LIST.TABLE_HEADER.CONTENT'),
    t('CANNED_MGMT.LIST.TABLE_HEADER.INBOXES'),
    t('CANNED_MGMT.LIST.TABLE_HEADER.ACTIONS'),
  ];
});

const getInboxIcon = inbox => {
  return getInboxClassByType(inbox.channel_type, inbox.phone_number);
};
</script>

<template>
  <!-- No `overflow-auto` here: SettingsWrapper already scrolls the page, and a
    scroll container starting at the content edge clips the 1px `outline` that
    the filter button and its dropdown draw outside their boxes. -->
  <div class="flex-1">
    <BaseSettingsHeader
      :title="$t('CANNED_MGMT.HEADER')"
      :description="$t('CANNED_MGMT.DESCRIPTION')"
      :link-text="$t('CANNED_MGMT.LEARN_MORE')"
      feature-name="canned_responses"
    >
      <template #actions>
        <Button
          icon="i-lucide-circle-plus"
          :label="$t('CANNED_MGMT.HEADER_BTN_TXT')"
          @click="openAddPopup"
        />
      </template>
    </BaseSettingsHeader>

    <div v-if="records.length" class="flex items-center gap-2 mt-6">
      <OnClickOutside @trigger="isInboxMenuOpen = false">
        <div class="relative group">
          <Button
            :label="activeInboxFilterLabel"
            size="sm"
            color="slate"
            icon="i-lucide-chevron-down"
            trailing-icon
            class="max-w-56"
            @click="isInboxMenuOpen = !isInboxMenuOpen"
          />
          <DropdownMenu
            v-if="isInboxMenuOpen"
            :menu-items="inboxMenuItems"
            show-search
            :search-placeholder="
              $t('CANNED_MGMT.LIST.FILTER.SEARCH_PLACEHOLDER')
            "
            class="left-0 w-56 mt-2 overflow-y-auto top-full max-h-60"
            @action="handleInboxFilter"
          />
        </div>
      </OnClickOutside>
    </div>

    <div class="mt-4 flex-1">
      <woot-loading-state
        v-if="uiFlags.fetchingList"
        :message="$t('CANNED_MGMT.LOADING')"
      />
      <p
        v-else-if="!filteredRecords.length"
        class="flex flex-col items-center justify-center h-full text-base text-slate-600 dark:text-slate-300 py-8"
      >
        {{
          records.length
            ? $t('CANNED_MGMT.SEARCH_404')
            : $t('CANNED_MGMT.LIST.404')
        }}
      </p>
      <table
        v-else
        class="min-w-full overflow-x-auto divide-y divide-slate-75 dark:divide-slate-700"
      >
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11 last:text-right"
          >
            <span v-if="thHeader !== tableHeaders[0]">
              {{ thHeader }}
            </span>
            <button
              v-else
              class="flex items-center p-0 cursor-pointer"
              @click="toggleSort"
            >
              <span class="mb-0">
                {{ thHeader }}
              </span>
              <fluent-icon
                class="ml-2 size-4"
                :icon="sortOrder === 'desc' ? 'chevron-up' : 'chevron-down'"
              />
            </button>
          </th>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr
            v-for="(cannedItem, index) in filteredRecords"
            :key="cannedItem.short_code"
          >
            <td
              class="py-4 ltr:pr-4 rtl:pl-4 truncate max-w-xs font-medium"
              :title="cannedItem.short_code"
            >
              {{ cannedItem.short_code }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 md:break-all whitespace-normal">
              {{ cannedItem.content }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div
                v-if="cannedItem.inboxes && cannedItem.inboxes.length > 0"
                class="flex flex-wrap gap-1"
              >
                <span
                  v-for="inbox in cannedItem.inboxes"
                  :key="inbox.id"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200"
                >
                  <fluent-icon
                    :icon="getInboxIcon(inbox)"
                    type="outline"
                    size="14"
                    class="text-slate-600 dark:text-slate-300"
                  />
                  <span>{{ inbox.internal_name || inbox.name }}</span>
                </span>
              </div>
              <span v-else class="text-slate-400 dark:text-slate-500 text-sm">
                {{ $t('CANNED_MGMT.LIST.ALL_INBOXES') }}
              </span>
            </td>
            <td class="py-4 flex justify-end gap-1">
              <Button
                v-tooltip.top="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
                icon="i-lucide-pen"
                slate
                xs
                faded
                @click="openEditPopup(cannedItem)"
              />
              <Button
                v-tooltip.top="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                :is-loading="loading[cannedItem.id]"
                @click="openDeletePopup(cannedItem, index)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <woot-modal v-model:show="showAddPopup" @close="hideAddPopup">
      <AddCanned :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" @close="hideEditPopup">
      <EditCanned
        v-if="showEditPopup"
        :id="activeResponse.id"
        :edshort-code="activeResponse.short_code"
        :edsubject="activeResponse.subject"
        :edcontent="activeResponse.content"
        :edfiles="activeResponse.files || []"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>
